/**
 * The one guard for the portal's WebGL scenes (the Home diorama and the Run Theater). Without WebGL, or when a scene
 * throws (a context the browser refused or lost reads back `null` attributes, which three's post-processing
 * dereferences), the scene renders nothing: the page around it, a run's questions and deliverables included, must
 * never fall into the router's error screen because a decoration could not draw.
 */
import { Component, type ReactNode } from 'react'

export class SilentBoundary extends Component<{ children: ReactNode; onError?: () => void }, { failed: boolean }> {
  override state = { failed: false }
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }
  override componentDidCatch(): void {
    this.props.onError?.()
  }
  override render(): ReactNode {
    return this.state.failed ? null : this.props.children
  }
}

/**
 * Probed ONCE per page load and the probe context is explicitly released —
 * browsers cap live WebGL contexts (~16) and force-lose the oldest, so a
 * per-mount leaked probe would eventually blank the diorama or the Run
 * Theater canvas itself.
 */
let webglProbe: boolean | undefined
export function supportsWebGL(): boolean {
  if (webglProbe !== undefined) return webglProbe
  try {
    const canvas = document.createElement('canvas')
    const gl = (canvas.getContext('webgl2') ?? canvas.getContext('webgl')) as WebGLRenderingContext | null
    webglProbe = Boolean(gl)
    gl?.getExtension('WEBGL_lose_context')?.loseContext()
  } catch {
    webglProbe = false
  }
  return webglProbe
}
